import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSITION_CLASSES = {
  top_left: 'top-20 left-4',
  top_right: 'top-20 right-4',
  bottom_left: 'bottom-24 left-4',
  bottom_right: 'bottom-24 right-4',
  center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
};

const CustomWidgets = ({ socketRef }) => {
  const [widgets, setWidgets] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    const fetchWidgets = async () => {
      try {
        const res = await fetch(`${API_URL}/api/nightclub/widgets`);
        if (res.ok) {
          const data = await res.json();
          setWidgets(data);
        }
      } catch (e) { /* silent */ }
    };
    fetchWidgets();
  }, []);

  // Listen for widget updates via socket
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    const handleWidgetUpdate = (data) => {
      if (data.action === 'added' && data.widget) {
        setWidgets(prev => {
          const filtered = prev.filter(w => w.widget_id !== data.widget.widget_id);
          return [...filtered, data.widget];
        });
      } else if (data.action === 'removed' && data.widget_id) {
        setWidgets(prev => prev.filter(w => w.widget_id !== data.widget_id));
      }
    };

    socket.on('widgets_updated', handleWidgetUpdate);
    return () => socket.off('widgets_updated', handleWidgetUpdate);
  }, [socketRef]);

  const dismissWidget = (widgetId) => {
    setDismissed(prev => new Set([...prev, widgetId]));
  };

  const visibleWidgets = widgets.filter(w => w.active && !dismissed.has(w.widget_id));

  return (
    <AnimatePresence>
      {visibleWidgets.map((widget) => (
        <motion.div
          key={widget.widget_id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`fixed z-30 max-w-xs ${POSITION_CLASSES[widget.position] || POSITION_CLASSES.top_right}`}
          data-testid={`widget-${widget.widget_id}`}
        >
          <div className="bg-gray-900/90 backdrop-blur-xl border border-purple-500/20 rounded-xl overflow-hidden shadow-lg shadow-purple-500/10">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-purple-500/10 bg-purple-500/5">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-purple-400" />
                <span className="text-[10px] font-semibold text-purple-300 uppercase tracking-wide">
                  {widget.title}
                </span>
              </div>
              <button
                onClick={() => dismissWidget(widget.widget_id)}
                className="text-white/30 hover:text-white/70 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            {/* Content */}
            <div
              className="p-3 text-sm text-white/80"
              dangerouslySetInnerHTML={{ __html: widget.content }}
            />
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  );
};

export default CustomWidgets;
